'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import {
  sanitizePostHogCapture,
  WEB_VITALS_CAPTURE_CONFIG,
} from '@/app/lib/posthogPerformance'
import {
  setPostHogClient,
  type PostHogClient,
} from '@/app/lib/posthogClient'

interface PostHogContextType {
  posthog: PostHogClient | null
  isLoaded: boolean
}

const PostHogContext = createContext<PostHogContextType>({
  posthog: null,
  isLoaded: false,
})

export const usePostHog = () => {
  const context = useContext(PostHogContext)
  if (!context) {
    throw new Error('usePostHog must be used within a PostHogProvider')
  }
  return context
}

interface PostHogProviderProps {
  children: React.ReactNode
}

export const PostHogProvider = ({ children }: PostHogProviderProps) => {
  const [posthog, setPostHog] = useState<PostHogClient | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST

    if (!key || !host) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('PostHog environment variables not found')
      }
      setIsLoaded(true)
      return
    }

    let cancelled = false
    let idleId: number | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const initialize = async () => {
      const { default: posthogClient } = await import('posthog-js')
      if (cancelled) return

      const finishLoading = () => {
        if (cancelled) return
        setPostHogClient(posthogClient)
        setPostHog(posthogClient)
        setIsLoaded(true)
      }

      if (posthogClient.__loaded) {
        finishLoading()
        return
      }

      posthogClient.init(key, {
        api_host: host,
        loaded: finishLoading,
        capture_pageview: false,
        capture_pageleave: true,
        capture_exceptions: true,
        capture_performance: WEB_VITALS_CAPTURE_CONFIG,
        capture_dead_clicks: false,
        disable_session_recording: true,
        disable_surveys: true,
        before_send: sanitizePostHogCapture,
      })
    }

    const scheduleInitialization = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => void initialize(), { timeout: 3_000 })
      } else {
        timeoutId = globalThis.setTimeout(() => void initialize(), 1_000)
      }
    }

    if (document.readyState === 'complete') {
      scheduleInitialization()
    } else {
      window.addEventListener('load', scheduleInitialization, { once: true })
    }

    return () => {
      cancelled = true
      window.removeEventListener('load', scheduleInitialization)
      if (idleId !== null) window.cancelIdleCallback(idleId)
      if (timeoutId !== null) globalThis.clearTimeout(timeoutId)
    }
  }, [])

  return (
    <PostHogContext.Provider value={{ posthog: isLoaded ? posthog : null, isLoaded }}>
      {children}
    </PostHogContext.Provider>
  )
}
